import styled from 'styled-components'

import { ReactComponent as AkiveLogo } from '../../../assets/images/akive_logo.svg'

export default function LegalRouteFallback({ variant }) {
  const isLicenses = variant === 'licenses'
  const isTerms = variant === 'terms'

  if (isLicenses) {
    return (
      <LicenseFallbackRoot role="status" aria-label="화면을 준비하는 중">
        <PrivacyFallbackTopbar>
          <PrivacyFallbackTopbarInner>
            <PrivacyFallbackBrand aria-hidden="true">
              <AkiveLogo />
            </PrivacyFallbackBrand>
          </PrivacyFallbackTopbarInner>
        </PrivacyFallbackTopbar>
        <LicenseFallbackMain aria-hidden="true">
          <LicenseFallbackHeader>
            <PrivacySkeleton $width="144px" $height="10px" />
            <PrivacySkeleton $width="min(320px, 78%)" $height="40px" />
            <PrivacySkeleton $width="min(560px, 94%)" />
            <PrivacySkeleton $width="220px" />
          </LicenseFallbackHeader>
          <LicenseSearchSkeleton />
          <LicenseListSkeleton>
            {Array.from({ length: 4 }).map((_, index) => (
              <LicenseCardSkeleton key={index} />
            ))}
          </LicenseListSkeleton>
        </LicenseFallbackMain>
      </LicenseFallbackRoot>
    )
  }

  return (
    <PrivacyFallbackRoot
      role="status"
      aria-label={isTerms ? '서비스 이용약관을 준비하는 중' : '개인정보 처리방침을 준비하는 중'}
    >
      <PrivacyFallbackTopbar>
        <PrivacyFallbackTopbarInner>
          <PrivacyFallbackBrand aria-hidden="true">
            <AkiveLogo />
          </PrivacyFallbackBrand>
          <PrivacyFallbackNav aria-hidden="true">
            <PrivacySkeleton $width="88px" $height="32px" />
            <PrivacySkeleton $width="84px" $height="32px" />
          </PrivacyFallbackNav>
        </PrivacyFallbackTopbarInner>
      </PrivacyFallbackTopbar>

      <PrivacyFallbackMain>
        <PrivacyFallbackHeader aria-hidden="true">
          <PrivacySkeleton $width="116px" $height="10px" />
          <PrivacySkeleton $width="min(380px, 82%)" $height="40px" />
          <PrivacySkeleton $width="min(460px, 92%)" />
          <PrivacyFallbackMeta>
            <PrivacySkeleton $width="44px" $height="28px" />
            <PrivacySkeleton $width="118px" />
            <PrivacySkeleton $width="118px" />
          </PrivacyFallbackMeta>
        </PrivacyFallbackHeader>

        <PrivacyFallbackContent aria-hidden="true">
          <PrivacyTocSkeleton>
            <PrivacySkeleton $width="38px" $height="18px" />
            {Array.from({ length: 9 }).map((_, index) => (
              <PrivacySkeleton
                key={index}
                $width={`${76 + (index % 3) * 8}%`}
                $height="13px"
              />
            ))}
          </PrivacyTocSkeleton>
          <PrivacyDocumentSkeleton>
            <PrivacySkeleton />
            <PrivacySkeleton $width="84%" />
            <PrivacySkeleton $width="min(440px, 72%)" $height="28px" />
            <PrivacyFallbackTable />
            <PrivacySkeleton $width="min(360px, 64%)" $height="28px" />
            <PrivacySkeleton />
            <PrivacySkeleton $width="74%" />
          </PrivacyDocumentSkeleton>
        </PrivacyFallbackContent>
      </PrivacyFallbackMain>
    </PrivacyFallbackRoot>
  )
}

const Skeleton = styled.div`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '15px'};
  margin-bottom: 16px;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.primarySoft};
`

const LicenseFallbackRoot = styled.div`
  min-height: 100dvh;
  background:
    radial-gradient(circle at 50% -220px, ${({ theme }) => theme.colors.primarySoft} 0, transparent 520px),
    ${({ theme }) => theme.colors.background};
`

const LicenseFallbackMain = styled.main`
  width: min(880px, calc(100% - 48px));
  margin: 0 auto;
  padding-bottom: 96px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    width: calc(100% - 32px);
    padding-bottom: 64px;
  }
`

const LicenseFallbackHeader = styled.header`
  display: grid;
  max-width: 780px;
  padding: 64px 0 42px;
  justify-items: start;
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    padding: 42px 0 32px;
  }
`

const LicenseSearchSkeleton = styled.div`
  height: 126px;
  margin-bottom: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
`

const LicenseListSkeleton = styled.div`
  display: grid;
  gap: 16px;
`

const LicenseCardSkeleton = styled.div`
  height: 86px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
`

const PrivacyFallbackRoot = styled.div`
  min-height: 100dvh;
  background: ${({ theme }) => theme.colors.background};
`

const PrivacyFallbackTopbar = styled.header`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.topBar};
`

const PrivacyFallbackTopbarInner = styled.div`
  display: flex;
  width: min(1180px, calc(100% - 48px));
  min-height: 68px;
  margin: 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 720px) {
    width: calc(100% - 32px);
    min-height: 62px;
  }
`

const PrivacyFallbackBrand = styled.div`
  color: ${({ theme }) => theme.colors.primary};

  svg {
    display: block;
    width: 92px;
    height: auto;
  }

  @media (max-width: 720px) {
    svg {
      width: 78px;
    }
  }
`

const PrivacyFallbackNav = styled.div`
  display: flex;
  gap: 4px;
`

const PrivacyFallbackMain = styled.main`
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
  padding-bottom: 96px;

  @media (max-width: 720px) {
    width: calc(100% - 32px);
    padding-bottom: 64px;
  }
`

const PrivacyFallbackHeader = styled.header`
  display: grid;
  max-width: 780px;
  padding: 64px 0 42px;
  justify-items: start;
  gap: 12px;

  @media (max-width: 720px) {
    padding: 42px 0 32px;
  }
`

const PrivacySkeleton = styled(Skeleton)`
  margin: 0;
`

const PrivacyFallbackMeta = styled.div`
  display: flex;
  margin-top: 12px;
  align-items: center;
  gap: 12px;

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const PrivacyFallbackContent = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  align-items: start;
  gap: 28px;

  @media (max-width: 1024px) {
    grid-template-columns: 220px minmax(0, 1fr);
    gap: 20px;
  }

  @media (max-width: 840px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const PrivacyTocSkeleton = styled.aside`
  display: grid;
  padding: 22px;
  gap: 15px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
`

const PrivacyDocumentSkeleton = styled.article`
  display: grid;
  min-width: 0;
  min-height: 680px;
  padding: clamp(24px, 5vw, 52px);
  align-content: start;
  gap: 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.low};
`

const PrivacyFallbackTable = styled.div`
  height: 208px;
  margin: 4px 0 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.small};
  background: repeating-linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.primarySoft} 0,
    ${({ theme }) => theme.colors.primarySoft} 42px,
    ${({ theme }) => theme.colors.border} 42px,
    ${({ theme }) => theme.colors.border} 43px,
    transparent 43px,
    transparent 84px
  );

  @media (max-width: 720px) {
    height: 176px;
  }
`
